/**
 * Appointment schemas — calendar write path + FHIR boundary validation.
 *
 * Enum arrays are tied to fhir-model types via `satisfies` so a mismatch
 * between frontend and server produces a compile error.
 */
import { z } from 'zod';
import type {
  AppointmentStatus as FhirAppointmentStatus,
  RecurringPattern as FhirRecurringPattern,
} from '@unify/fhir-model/resources/appointment';

export const appointmentIdSchema = z.string().min(1, 'Appointment id is required');

/** Calendar UI status values accepted by saveCalendarAppointment. */
export const calendarStatusValues = [
  'proposed',
  'scheduled',
  'attended',
  'client-cancelled',
  'client-late-cancelled',
  'clinician-cancelled',
  'client-no-show',
] as const;

/** FHIR Appointment.status values (Unify subset). */
export const fhirAppointmentStatusValues = [
  'proposed',
  'booked',
  'fulfilled',
  'cancelled',
  'noshow',
] as const satisfies readonly FhirAppointmentStatus[];

/** Appointment modality values per PRD. */
export const appointmentModalityValues = ['in-person', 'telehealth', 'hybrid'] as const;

/** Calendar sub-category stored in serviceCategory. */
export const appointmentCategoryValues = ['session', 'group', 'meeting', 'time-entry'] as const;

/** Recurring pattern codes — synced with fhir-model. */
export const recurringPatternValues = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
] as const satisfies readonly FhirRecurringPattern[];

/** Additional participant beyond the primary practitioner and patient. */
const additionalParticipantSchema = z.object({
  resourceType: z.enum(['Practitioner', 'Patient', 'RelatedPerson']),
  id: z.string().min(1),
  display: z.string().optional(),
});

/**
 * Write schema: `SaveCalendarAppointmentArgs` from `saveCalendarAppointment.ts`.
 */
export const saveCalendarAppointmentSchema = z.object({
  appointmentId: z.string().optional(),
  start: z.date(),
  end: z.date(),
  calendarStatus: z.enum(calendarStatusValues),
  practitionerId: z.string().min(1, 'Provider is required'),
  practitionerDisplay: z.string().optional(),
  siteId: z.string().optional(),
  patientId: z.string().optional(),
  patientDisplay: z.string().optional(),
  episodeOfCareId: z.string().optional(),
  cptCode: z.string().optional(),
  cptDisplay: z.string().optional(),
  appointmentCategory: z.enum(appointmentCategoryValues).optional(),
  modality: z.enum(appointmentModalityValues).optional(),
  description: z.string().optional(),
  videoLink: z.string().url().optional().or(z.literal('')),
  recurringPattern: z.enum([...recurringPatternValues, 'none']).optional(),
  additionalParticipants: z.array(additionalParticipantSchema).optional(),
  therapyGroupId: z.string().optional(),
  therapyGroupDisplay: z.string().optional(),
}).refine(
  (d) => d.end.getTime() > d.start.getTime(),
  { message: 'End time must be after start time', path: ['end'] },
);

export type SaveCalendarAppointmentInput = z.infer<typeof saveCalendarAppointmentSchema>;

const timeLabelSchema = z.string().min(1, 'Time is required');
const nullableDateSchema = z.date().nullable();

const appointmentFormSharedSchema = z.object({
  eventType: z.enum(['appointment', 'meeting', 'task', 'blocked']),
  title: z.string(),
  description: z.string(),
  date: nullableDateSchema,
  startTime: timeLabelSchema,
  endTime: timeLabelSchema,
  attendanceStatus: z.enum(calendarStatusValues),
  provider: z.string(),
  facilitator: z.string(),
  coFacilitators: z.array(z.string()),
  client: z.string().nullable(),
  /** Session attendees as full refs (`Patient/{id}`, `RelatedPerson/{id}`) —
   * the client plus the episode's `participants` extension members. */
  sessionAttendees: z.array(z.string()),
  clients: z.array(z.string()),
  clientStatuses: z.record(z.string(), z.string()),
  modality: z.enum(appointmentModalityValues),
  siteId: z.string(),
  officeForTelehealth: z.string(),
  telehealthFromOffice: z.boolean(),
  roomId: z.string().nullable(),
  deviceIds: z.array(z.string()),
  recurring: z.enum(['none', ...recurringPatternValues]),
  videoLink: z.string(),
  episodeOfCareId: z.string().nullable(),
  groupType: z.string(),
  /** Therapy cohort Group.id for one-off group sessions. */
  therapyGroupId: z.string(),
  meetingType: z.string(),
  meetingHost: z.string(),
  meetingParticipants: z.array(z.string()),
  meetingDescription: z.string(),
  timeEntryType: z.string(),
  timeEntryNotes: z.string(),
  sessionType: z.string(),
  program: z.string(),
  recurringEnabled: z.boolean(),
  recurringInterval: z.number().int().min(1),
  recurringTimeframe: z.string(),
  recurringDaysOfWeek: z.array(z.string()),
  recurringEndType: z.enum(['occurrences', 'date']),
  recurringEndDate: nullableDateSchema,
  recurringOccurrences: z.number().int().min(1),
});

type AppointmentFormSharedValues = z.infer<typeof appointmentFormSharedSchema>;

function parseTimeLabel(label: string): number | null {
  const match = label.match(/^(\d{1,2}):(\d{2})\s(AM|PM)$/);
  if (!match) return null;
  const hour12 = Number(match[1]);
  const minute = Number(match[2]);
  const period = match[3];
  if (!Number.isFinite(hour12) || !Number.isFinite(minute) || hour12 < 1 || hour12 > 12 || minute < 0 || minute > 59) {
    return null;
  }
  const hour24 = hour12 % 12 + (period === 'PM' ? 12 : 0);
  return hour24 * 60 + minute;
}

function withSharedRefinements<T extends z.ZodTypeAny>(schema: T): T {
  return schema.superRefine((rawValue: unknown, ctx) => {
    const value = rawValue as AppointmentFormSharedValues & { appointmentCategory: string };
    const start = parseTimeLabel(value.startTime);
    const end = parseTimeLabel(value.endTime);

    if (!value.date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['date'],
        message: 'Date is required',
      });
    }

    if (start === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startTime'],
        message: 'Start time is invalid',
      });
    }

    if (end === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endTime'],
        message: 'End time is invalid',
      });
    }

    if (start !== null && end !== null && end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endTime'],
        message: 'End time must be after start time',
      });
    }

    if (value.recurringEnabled && value.recurring === 'none') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurring'],
        message: 'Recurring pattern is required',
      });
    }

    if (value.recurringEnabled && value.recurringEndType === 'date' && !value.recurringEndDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringEndDate'],
        message: 'Recurring end date is required',
      });
    }

    if (value.recurringEnabled && value.recurringTimeframe === 'week' && value.recurringDaysOfWeek.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['recurringDaysOfWeek'],
        message: 'Select at least one day',
      });
    }

    if (value.appointmentCategory !== 'time-entry') {
      if (!value.provider && value.appointmentCategory === 'session') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['provider'],
          message: 'Provider is required',
        });
      }

      if (value.modality !== 'telehealth' && (!value.siteId || value.siteId === 'telehealth')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['siteId'],
          message: 'Location is required',
        });
      }

      if (value.modality === 'telehealth' && value.telehealthFromOffice && !value.officeForTelehealth) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['officeForTelehealth'],
          message: 'Office is required',
        });
      }
    }
  }) as T;
}

const sessionAppointmentFormSchema = withSharedRefinements(
  appointmentFormSharedSchema.extend({
    appointmentCategory: z.literal('session'),
    sessionType: z.string().min(1, 'Session type is required'),
    /** Performing provider's Practitioner id (sessions only; group/meeting keep display names). */
    provider: z.string().min(1, 'Provider is required'),
  }).superRefine((value, ctx) => {
    if (!value.episodeOfCareId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['episodeOfCareId'],
        message: 'Client is required',
      });
    }
  })
);

const groupAppointmentFormSchema = withSharedRefinements(
  appointmentFormSharedSchema.extend({
    appointmentCategory: z.literal('group'),
    therapyGroupId: z.string().min(1, 'Group cohort is required'),
    facilitator: z.string().min(1, 'Facilitator is required'),
    clients: z.array(z.string()).min(1, 'Select at least one client'),
  })
);

const meetingAppointmentFormSchema = withSharedRefinements(
  appointmentFormSharedSchema.extend({
    appointmentCategory: z.literal('meeting'),
    meetingType: z.string().min(1, 'Meeting type is required'),
    meetingHost: z.string().min(1, 'Host is required'),
  })
);

const timeEntryAppointmentFormSchema = withSharedRefinements(
  appointmentFormSharedSchema.extend({
    appointmentCategory: z.literal('time-entry'),
    timeEntryType: z.string().min(1, 'Time entry type is required'),
    timeEntryNotes: z.string().trim().min(1, 'Notes are required'),
  })
);

export const appointmentFormSchema = z.discriminatedUnion('appointmentCategory', [
  sessionAppointmentFormSchema,
  groupAppointmentFormSchema,
  meetingAppointmentFormSchema,
  timeEntryAppointmentFormSchema,
]);

export type AppointmentFormValues = z.infer<typeof appointmentFormSchema>;

export const defaultAppointmentFormValues = (defaults?: Partial<AppointmentFormValues>): AppointmentFormValues => ({
  appointmentCategory: 'session',
  eventType: 'appointment',
  title: '',
  description: '',
  date: null,
  startTime: '9:00 AM',
  endTime: '10:00 AM',
  attendanceStatus: 'scheduled',
  provider: '',
  facilitator: '',
  coFacilitators: [],
  client: null,
  sessionAttendees: [],
  clients: [],
  clientStatuses: {},
  modality: 'telehealth',
  siteId: '',
  officeForTelehealth: '',
  telehealthFromOffice: false,
  roomId: null,
  deviceIds: [],
  recurring: 'none',
  videoLink: '',
  episodeOfCareId: null,
  groupType: '',
  therapyGroupId: '',
  meetingType: '',
  meetingHost: '',
  meetingParticipants: [],
  meetingDescription: '',
  timeEntryType: '',
  timeEntryNotes: '',
  sessionType: '',
  program: 'outpatient',
  recurringEnabled: false,
  recurringInterval: 1,
  recurringTimeframe: 'week',
  recurringDaysOfWeek: [],
  recurringEndType: 'occurrences',
  recurringEndDate: null,
  recurringOccurrences: 10,
  ...defaults,
});

/** Intake appointment creation (lighter than full calendar). */
export const intakeAppointmentSchema = z.object({
  status: z.enum(fhirAppointmentStatusValues),
  start: z.string().min(1, 'Start time is required'),
  end: z.string().min(1, 'End time is required'),
  description: z.string().optional(),
  patientId: z.string().min(1, 'Patient is required'),
  practitionerId: z.string().optional(),
  serviceTypeDisplay: z.string().optional(),
});

export type IntakeAppointmentInput = z.infer<typeof intakeAppointmentSchema>;
